class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.closeTimeout = null;
    this.openTimeout = null;

    // bound handlers (no underscores)
    this.openOnHoverHandler = this.openOnHover.bind(this);
    this.closeOnHoverHandler = this.closeOnHover.bind(this);
    this.clickHandler = this.handleClick.bind(this);
    this.eventsBound = false;

    // Bind click events for all screen sizes, hover events only for desktop
    this.bindEvents();

    // re-bind / un-bind hover events on resize
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1024 && !this.eventsBound) {
        this.bindDesktopEvents();
      } else if (window.innerWidth < 1024 && this.eventsBound) {
        this.unbindDesktopEvents();
      }
    });
  }

  bindEvents() {
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.summary.addEventListener('click', this.clickHandler);
    this.keyboardToggle = this.mainDetailsToggle.querySelector('.js-details-toggle');
    if (this.keyboardToggle) {
      this.keyboardToggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.toggleMenuState(this.mainDetailsToggle.open);
        }
      });
      this.keyboardToggle.addEventListener('keyup', (e) => {
        if (e.key === ' ') {
          e.preventDefault();
          this.toggleMenuState(this.mainDetailsToggle.open);
        }
      });
    }

    // Add desktop hover events if screen is large enough
    if (window.innerWidth >= 1024) {
      this.bindDesktopEvents();
    }
  }

  bindDesktopEvents() {
    this.addEventListener('mouseenter', this.openOnHoverHandler);
    this.addEventListener('mouseleave', this.closeOnHoverHandler);
    this.eventsBound = true;
  }

  unbindDesktopEvents() {
    this.removeEventListener('mouseenter', this.openOnHoverHandler);
    this.removeEventListener('mouseleave', this.closeOnHoverHandler);

    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    this.classList.remove('closing');

    this.eventsBound = false;
  }

  openOnHover() {
    if (window.innerWidth < 1024) return;

    // Cancel any pending open timer (e.g. re-entering quickly)
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    this.classList.remove('closing');

    // If this menu is already open, we still needed to cancel any pending close above.
    // After cleanup, there's nothing else to do.
    if (this.mainDetailsToggle && this.mainDetailsToggle.open) return;

    // Only require hover intent delay when *initially* opening the mega menu.
    // If any header menu is already open, switching between menus should be instant.
    const anyMenuOpen = !!document.querySelector('header-menu details[open]');

    const openImmediately = () => {
      this.closeOtherMenus();
      this.mainDetailsToggle.setAttribute('open', '');
      this.summary.setAttribute('aria-expanded', 'true');
      this.onToggle();
    };

    if (anyMenuOpen) {
      openImmediately();
      return;
    }

    this.openTimeout = setTimeout(() => {
      this.openTimeout = null;
      if (window.innerWidth < 1024) return;
      if (!this.isConnected) return;
      // Don't open if the pointer has already left before the delay elapsed.
      if (!this.matches(':hover') && !this.keyboardToggle?.matches(':focus')) return;
      openImmediately();
    }, 300);
  }

  closeOtherMenus() {
    const allHeaderMenus = document.querySelectorAll('header-menu');

    allHeaderMenus.forEach((menu) => {
      if (menu === this) return;

      if (menu.openTimeout) {
        clearTimeout(menu.openTimeout);
        menu.openTimeout = null;
      }

      if (menu.closeTimeout) {
        clearTimeout(menu.closeTimeout);
        menu.closeTimeout = null;
      }

      if (menu.mainDetailsToggle && menu.mainDetailsToggle.hasAttribute('open')) {
        menu.classList.remove('closing');
        menu.close();
      }
    });
  }

  closeOnHover() {
    if (window.innerWidth < 1024) return;

    // If an open is pending, cancel it (prevents accidental opens when scrolling/passing by)
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }

    this.classList.add('closing');

    this.closeTimeout = setTimeout(() => {
      this.close();
      this.classList.remove('closing');
      this.closeTimeout = null;
    }, 200);
  }

  handleClick(event) {
    if (window.innerWidth >= 1024) {
      // Desktop behavior: prevent default and navigate if there's a href
      event.preventDefault();
      const linkUrl = this.summary.dataset.href;
      if (linkUrl) {
        window.location.href = linkUrl;
      }
    }
    // Mobile behavior: allow default details/summary toggle functionality
    // The browser will automatically toggle the 'open' attribute
  }

  toggleMenuState(isOpen) {
    if (!isOpen) {
      this.openOnHoverHandler();
    } else {
      this.closeOnHoverHandler();
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;
    const existing = document.documentElement.style.getPropertyValue('--header-bottom-position-desktop');
    if (!existing) {
      const bottomY = Math.floor(this.header.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--header-bottom-position-desktop', `${bottomY}px`);
    }
  }
}

customElements.define('header-menu', HeaderMenu);
